/**
 * 百度智能云 Portal 集成：登录态检测 + 订单抽屉（购买弹窗）。
 * 登录态自建：JSONP 拉 /api/account/v2/displayName（整页只发一条），登录弹窗用 iframe + postMessage。
 * 不再加载 portal-common —— 它只是把同一条 displayName 再拉一遍（headerDisplayNameCallBack*），
 * 且其 header/footer 等 UI 本站都不用；下单抽屉 order-drawer 仍在点击「立即购买」时按需加载。
 * 仅在浏览器端调用。环境沿用运行时 hostname 判断（sandbox / online）。
 */

import { getCookie, setCookie, delCookie, getAllParamsFromUrl } from "@/common/helper";

type AnyObj = Record<string, any>;

/** 门户登录态：与 portal-common 的 info 结构对齐 */
export interface PortalUserInfo {
  displayName?: string;
  accountId?: string;
  hasLogin: boolean;
}

let currentUserInfo: PortalUserInfo = { hasLogin: false };

/** 登录态订阅者：登录/退出或首次拉取后统一通知，驱动 Header 等 UI 更新 */
const userSubscribers = new Set<(info: PortalUserInfo) => void>();

function notifyUserSubscribers() {
  userSubscribers.forEach((fn) => {
    try {
      fn(currentUserInfo);
    } catch (err) {
      console.warn("[opc tokens] user subscriber error:", err);
    }
  });
}

function getPortalEnv(): "sandbox" | "online" {
  if (typeof location === "undefined") return "online";
  return /sandbox|test|localhost|127\.0\.0\.1|\.baidu-int\.com/.test(location.hostname) ? "sandbox" : "online";
}

function loadStyleOnce(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const links = document.querySelectorAll('link[rel="stylesheet"]');
    for (let i = 0; i < links.length; i++) {
      if ((links[i] as HTMLLinkElement).href === url) {
        resolve();
        return;
      }
    }
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = url;
    link.onload = () => resolve();
    link.onerror = reject;
    document.head.appendChild(link);
  });
}

function loadScriptOnce(url: string, globalChecker?: () => boolean): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof globalChecker === "function" && globalChecker()) {
      resolve();
      return;
    }
    const existed = Array.prototype.find.call(document.scripts, (script: HTMLScriptElement) => script.src === url) as
      | HTMLScriptElement
      | undefined;
    if (existed) {
      existed.addEventListener("load", () => resolve(), { once: true });
      existed.addEventListener("error", reject, { once: true });
      if (typeof globalChecker === "function" && globalChecker()) resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = url;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

function normalizeLoginInfo(info: AnyObj): PortalUserInfo {
  const r = info && (info.result || info.data || info);
  if (r && typeof r.hasLogin !== "undefined") {
    return { hasLogin: !!r.hasLogin, displayName: r.displayName || "", accountId: r.accountId || "" };
  }
  return { hasLogin: false };
}

/**
 * account v2 登录态同步：把 displayName 接口下发的 cookies 回种到当前站点域，与
 * cloud-market-frontend 的 initInfo 保持一致。使 opc.baidu.com 与 bce 域共享登录态。
 * 仅在浏览器端执行；bce(test).baidu.com 及 localhost 本身不需要回种。
 */
function syncAccountV2Cookies(res: AnyObj): void {
  if (typeof document === "undefined") return;
  const r = res && (res.result || res.data || res);
  const cookies: Record<string, string> | undefined = r?.cookies;
  if (!cookies) return;

  // 除 bce 域外的其他域都启用 v2（bce 域自身即种植源，无需回种）
  const bceDomains = ["bce.baidu.com", "bcetest.baidu.com"];
  const enableAccountV2 = !bceDomains.includes(location.host) && !location.host.includes("localhost");
  if (!enableAccountV2) return;

  // 一般外部域名为 .baidu.com、内部域名为 .baidu-int.com；个别不符规律的域用 domainList 兜底判断
  const domainList = ["cloudtest.baidu.com", "qasandbox.bcetest.baidu.com"];
  const apiHost =
    !domainList.includes(location.host) && location.host.includes(".baidu.com")
      ? "bce.baidu.com"
      : "bcetest.baidu.com";

  // 带 track 参数时，加载 apiHost 下的 x.html，由后台完成 CAMPAIGN 相关 cookie 的跨域种植
  const track = getAllParamsFromUrl().track;
  if (track) {
    const iframeEle = document.createElement("iframe");
    const iframeUrl = "//" + apiHost + "/helper/x.html" + location.search + "&.stamp=" + Date.now();
    iframeEle.src = iframeUrl;
    iframeEle.style.display = "none";
    document.body.appendChild(iframeEle);
  }

  for (const key in cookies) {
    if (!Object.prototype.hasOwnProperty.call(cookies, key)) continue;
    const isTracking = key === "CAMPAIGN_TRACK" || key === "CAMPAIGN_TRACK_TIME";
    // tracking cookie 已存在则不覆盖
    if (isTracking && getCookie(key)) continue;
    const value = cookies[key];
    if (value) {
      // tracking cookie 保留 90 天，其余为会话级；统一种到 .{hostname} 以跨子域共享
      setCookie(key, value, isTracking ? 90 : null, false, `.${location.hostname}`);
      // bce-user-info 额外按加引号形式在当前域再种一次，兼容读取方
      if (key === "bce-user-info") {
        setCookie(key, `"${value}"`, null, false);
      }
    } else if (!isTracking) {
      delCookie(key, `.${location.hostname}`);
    }
  }
}

/**
 * 百度智能云统一登录页 iframe 地址（弹窗内嵌），按 hostname 判断线上/沙盒。
 * 2024.03.14 起 IAM 内置，直接用登录页根地址即可，无需再带 redirect 参数。
 * 前提：opc.baidu.com 已在 login 页 CSP frame-ancestors 白名单内（IAM 侧已配置），iframe 才能加载。
 */
function getLoginIframeUrl(): string {
  return getPortalEnv() === "sandbox" ? "https://login.bcetest.baidu.com" : "https://login.bce.baidu.com";
}

/** 是否移动端 UA。移动端登录页走 WAP 流程，未加入 opc 官网的 frame-ancestors 白名单，iframe 内嵌会空白。 */
function isMobile(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile|Windows Phone|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * 移动端整页跳转登录：跳统一登录页并带 redirect 回当前页，登录成功后由登录页 302 回跳。
 * 替代 iframe 弹窗（移动端 iframe 会因 frame-ancestors/X-Frame-Options 限制渲染空白）。
 */
function redirectToLogin(): void {
  if (typeof location === "undefined") return;
  const redirect = location.href.replace(location.hash, "") + location.hash;
  location.href = `${getLoginIframeUrl()}/?redirect=${encodeURIComponent(redirect)}`;
}

/**
 * displayName 接口域名：按运行时 hostname 判断（opc.baidu.com 命中 online）。
 * 直接由浏览器发 JSONP，请求会带上 .baidu.com 域的 Cookie（BDUSS_BFESS/bce-sessionid
 * 均为 SameSite=None，跨站会随请求发送），据此拿到真实登录态。
 * 不走服务端代理：生产 Node 服务无公网出口，代不出去（恒 502）。
 */
function getDisplayNameUrl(): string {
  const host = getPortalEnv() === "sandbox" ? "https://bcetest.baidu.com" : "https://bce.baidu.com";
  return host + "/api/account/v2/displayName";
}

/** JSONP 跨域拉取登录态：动态 <script> + 回调，短超时兜底，避免登录流程被网络拖住。 */
function jsonp<T = AnyObj>(url: string, timeout = 6000): Promise<T> {
  return new Promise((resolve, reject) => {
    const cbName = `opcLoginCb_${Date.now()}_${Math.floor(Math.random() * 1e5)}`;
    const script = document.createElement("script");
    let timer: ReturnType<typeof setTimeout>;
    const cleanup = () => {
      delete (window as AnyObj)[cbName];
      if (script.parentNode) script.parentNode.removeChild(script);
      if (timer) clearTimeout(timer);
    };
    (window as AnyObj)[cbName] = (data: T) => {
      cleanup();
      resolve(data);
    };
    script.onerror = () => {
      cleanup();
      reject(new Error("jsonp request failed"));
    };
    timer = setTimeout(() => {
      cleanup();
      reject(new Error("jsonp timeout"));
    }, timeout);
    const sep = url.includes("?") ? "&" : "?";
    script.src = `${url}${sep}callback=${cbName}`;
    document.body.appendChild(script);
  });
}

// 本次页面加载内已发起的登录态请求：整页只发一条 JSONP。
// 不在完成后释放——避免 StrictMode 双跑 / 多个 Header 订阅时，第一条请求已返回、
// 第二次挂载又发出第二条，造成「偶发两遍、其中一遍(白名单)返回未登录把状态打回」。
// 页面刷新时模块重载、此缓存自然清空，会重新拉取一次；登录成功后的回填走 forceRefresh。
let userInfoPromise: Promise<PortalUserInfo> | null = null;

function fetchUserInfo(): Promise<PortalUserInfo> {
  return jsonp<AnyObj>(getDisplayNameUrl())
    .then((res) => {
      // 与 cloud-market-frontend initInfo 一致：先把接口下发的 cookies 回种到当前域，同步登录态
      syncAccountV2Cookies(res);
      const info = normalizeLoginInfo(res);
      // displayName 接口缺官网 Referer 白名单时会恒返回 hasLogin:false；此时若已通过 iframe
      // postMessage 确认登录，则保留已登录态，只在接口真的判定已登录时才整体覆盖，避免把
      // 刚登录的 Header 又打回未登录。
      if (info.hasLogin || !currentUserInfo.hasLogin) {
        currentUserInfo = info;
      } else if (info.displayName) {
        currentUserInfo = { ...currentUserInfo, displayName: info.displayName, accountId: info.accountId };
      }
      notifyUserSubscribers();
      return currentUserInfo;
    })
    .catch((err) => {
      console.warn("[opc tokens] get login status error:", err);
      return currentUserInfo;
    });
}

/**
 * 拉取登录态。整页缓存，重复调用共享同一次 JSONP（含 StrictMode 双跑）。
 * forceRefresh=true 时强制重新拉取一次（用于 iframe 登录成功后回填真实用户名）。
 */
function getUserInfoOnce(forceRefresh = false): Promise<PortalUserInfo> {
  // 浏览器直发 JSONP 拉取登录态（带 .baidu.com Cookie）。失败/超时视为未登录，不阻塞后续流程。
  if (typeof document === "undefined") return Promise.resolve(currentUserInfo);
  if (!forceRefresh && userInfoPromise) return userInfoPromise;
  userInfoPromise = fetchUserInfo();
  return userInfoPromise;
}

let loginModalOpen = false;

/**
 * 打开内嵌 iframe 登录弹窗（PC 端，替代整页跳转）。
 * iframe 指向统一登录页；登录成功后登录页会向父页 postMessage({loginSuccess:true})，
 * 新账号激活成功则 postMessage({activateSuccess:true})。收到后关闭弹窗并回读登录态。
 * 注意：移动端登录页 WAP 流程不在 opc 官网 frame-ancestors 白名单内，iframe 会渲染空白，
 * 故移动端不走此弹窗，改由 ensureLogin 整页跳转（见 redirectToLogin）。
 */
function openLoginModal(onSuccess: (info: PortalUserInfo) => void, onCancel?: () => void, onToast?: (m: string) => void) {
  if (typeof document === "undefined") {
    onToast?.("登录环境不可用，请刷新页面后重试");
    return;
  }
  if (loginModalOpen) return;
  loginModalOpen = true;

  const overlay = document.createElement("div");
  overlay.setAttribute("data-opc-login-modal", "1");
  overlay.style.cssText =
    "position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,.45);" +
    "display:flex;align-items:center;justify-content:center;";

  const panel = document.createElement("div");
  panel.style.cssText =
    "position:relative;width:600px;height:600px;max-width:92vw;max-height:92vh;" +
    "background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,.24);";

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.setAttribute("aria-label", "关闭登录");
  closeBtn.textContent = "×";
  closeBtn.style.cssText =
    "position:absolute;top:8px;right:12px;z-index:1;border:none;background:transparent;" +
    "font-size:24px;line-height:1;cursor:pointer;color:#666;";

  const iframe = document.createElement("iframe");
  iframe.src = getLoginIframeUrl();
  iframe.style.cssText = "width:100%;height:100%;border:none;display:block;";

  let closed = false;
  const close = (cancelled: boolean) => {
    if (closed) return;
    closed = true;
    loginModalOpen = false;
    window.removeEventListener("message", onMessage);
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    if (cancelled) onCancel?.();
  };

  const onMessage = (event: MessageEvent) => {
    // 仅接受来自登录页 origin 的消息，避免误信任意页面
    if (event.origin !== getLoginIframeUrl()) return;
    const data = event.data as AnyObj;
    if (!data || typeof data !== "object") return;
    if (data.loginSuccess === true || data.activateSuccess === true) {
      close(false);
      // 以登录页 postMessage 的 loginSuccess 为登录态权威信号：乐观置为已登录。
      // opc.baidu.com 既读不到 .bce.baidu.com 的 httpOnly cookie，displayName JSONP 又缺官网
      // Referer 白名单，故此处不依赖它们判定；仅在白名单补齐后由 getUserInfoOnce 回填真实用户名。
      currentUserInfo = { ...currentUserInfo, hasLogin: true };
      notifyUserSubscribers();
      onSuccess(currentUserInfo);
      getUserInfoOnce(true).catch(() => {
        /* 白名单未开时忽略回填失败 */
      });
    }
  };

  closeBtn.addEventListener("click", () => close(true));
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close(true);
  });
  window.addEventListener("message", onMessage);

  panel.appendChild(closeBtn);
  panel.appendChild(iframe);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);
}

function ensureLogin(callback: (info: PortalUserInfo) => void, onToast?: (m: string) => void) {
  // 已有缓存登录态（订阅时已异步拉取过）时直接放行，不再等网络。
  if (currentUserInfo.hasLogin) {
    callback(currentUserInfo);
    return;
  }
  // 移动端：登录页 WAP 流程不允许被 iframe 内嵌（会空白），改为整页跳转 + redirect 回当前页。
  // 跳转后本页卸载，callback 不再执行；登录成功由登录页 302 回跳，回来后 subscribeUser 重新拉取登录态。
  if (isMobile()) {
    redirectToLogin();
    return;
  }
  // PC：打开内嵌 iframe 登录弹窗，登录成功后由 postMessage 回调驱动 callback。
  openLoginModal(callback, undefined, onToast);
}

function openOrderDrawer(campaignId: string, onToast?: (m: string) => void) {
  const drawerBaseUrl = "https://bce.bdstatic.com/fe-static/baidu/bce-portal/order-drawer/" + getPortalEnv();
  const campaignIdObj = {
    groupId: "",
    campaignIdRequests: [{ campaignId, countNum: 1 }],
    campaignCall: true,
  };

  Promise.all([
    loadStyleOnce(drawerBaseUrl + "/order-drawer.css"),
    loadScriptOnce(
      drawerBaseUrl + "/order-drawer.umd.js",
      () =>
        !!(
          (window as AnyObj).commonOrderDrawer &&
          typeof (window as AnyObj).commonOrderDrawer.showCampaignOrderDrawer === "function"
        )
    ),
  ])
    .then(() => {
      const w = window as AnyObj;
      if (w.commonOrderDrawer && typeof w.commonOrderDrawer.showCampaignOrderDrawer === "function") {
        w.commonOrderDrawer.showCampaignOrderDrawer(campaignIdObj, { mode: "drawer", source: "campaign" });
      } else {
        onToast?.("订单抽屉组件未加载，请稍后重试");
      }
    })
    .catch((err) => {
      console.warn("[opc tokens] order drawer error:", err);
      onToast?.("订单抽屉组件加载失败，请刷新页面后重试");
    });
}

/** 同步读取当前缓存的登录态（供表单提交时附带账号信息，仅运营参考用途） */
export function getCurrentUser(): PortalUserInfo {
  return currentUserInfo;
}

/**
 * 订阅登录态变化。立即用当前缓存值回调一次，随后每次登录态更新都会回调。
 * 首次订阅时自动拉取一次真实登录态（走本文件的 JSONP，多个订阅者合并为一条请求）。
 * 返回取消订阅函数。
 */
export function subscribeUser(fn: (info: PortalUserInfo) => void): () => void {
  userSubscribers.add(fn);
  fn(currentUserInfo);
  // 拉取真实登录态（合并请求，多个订阅者只发一次）
  getUserInfoOnce().catch((err) => console.warn("[opc tokens] get login status error:", err));
  return () => {
    userSubscribers.delete(fn);
  };
}

/** 主动拉起登录。PC 端弹 iframe 弹窗；移动端整页跳登录页并带 redirect 回跳。 */
export function login(onSuccess?: (info: PortalUserInfo) => void, onToast?: (m: string) => void) {
  ensureLogin((info) => onSuccess?.(info), onToast);
}

/**
 * 登录守卫：已登录直接执行 action；未登录先弹登录框，登录成功后再执行。
 * 用于「免费入驻 / 申请入驻 / 机构入驻」等需登录态的入口点击。
 */
export function requireLogin(action: (info: PortalUserInfo) => void, onToast?: (m: string) => void) {
  ensureLogin(action, onToast);
}

/** 退出登录：跳转门户统一登出地址，登出后回跳当前页。 */
export function logout() {
  if (typeof location === "undefined") return;
  const sandbox = getPortalEnv() === "sandbox";
  const redirect = encodeURIComponent(location.href.replace(location.hash, "")) + location.hash;
  location.href = `https://login.bce${sandbox ? "test" : ""}.baidu.com/logout?redirect=${redirect}`;
}

/** 立即购买：校验登录态后拉起订单抽屉。缺少 campaignId 或加载失败时通过 onToast 提示。 */
export function purchaseCampaign(campaignId: string, onToast?: (m: string) => void) {
  if (!campaignId) {
    onToast?.("活动配置缺少 campaignId");
    return;
  }
  ensureLogin(() => openOrderDrawer(campaignId, onToast), onToast);
}
